'use client';

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PackagePlus } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Modal, ModalBody, ModalFooter } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/Toast';
import { apiErrorMessage, ElowenApiError, elowenClient } from '../../lib/elowenClient';
import { useTranslation } from '../../lib/i18n';
import { QUERY_KEYS, useMe } from '../../lib/queries';
import { pluginDisplayName } from './pluginDisplayName';
import { PluginIcon } from './PluginIcon';
import { STARTER_OFFER_QUERY_KEY } from '../../components/licence/LicenceControls';

export function useStarterOffer() {
  const admin = useMe().data?.user?.is_admin === true;
  return useQuery({ queryKey: STARTER_OFFER_QUERY_KEY, queryFn: elowenClient.starterOffer, enabled: admin, retry: false });
}

export function StarterOfferDialog({ automatic = false, onClose }: { automatic?: boolean; onClose?: () => void }) {
  const offer = useStarterOffer();
  const { t, locale } = useTranslation();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [withCore, setWithCore] = useState(false);
  if (!offer.data || (automatic && offer.data.state !== 'offered')
    || (!automatic && !['offered', 'dismissed'].includes(offer.data.state ?? ''))) return null;

  const dismiss = async () => {
    if (!automatic) { onClose?.(); return; }
    setBusy(true);
    try {
      await elowenClient.dismissStarter();
      await qc.invalidateQueries({ queryKey: STARTER_OFFER_QUERY_KEY });
    } catch (error) {
      toast(apiErrorMessage(error), 'error');
    } finally { setBusy(false); }
  };
  const install = async () => {
    setBusy(true);
    try {
      await elowenClient.installStarter(withCore);
      await Promise.all([
        qc.invalidateQueries({ queryKey: STARTER_OFFER_QUERY_KEY }),
        qc.invalidateQueries({ queryKey: QUERY_KEYS.systemUpdate }),
      ]);
      onClose?.();
    } catch (error) {
      if (error instanceof ElowenApiError && error.code === 'core update preparation required') { setWithCore(true); return; }
      toast(apiErrorMessage(error), 'error');
      await qc.invalidateQueries({ queryKey: STARTER_OFFER_QUERY_KEY });
    } finally { setBusy(false); }
  };
  return (
    <Modal title={t.licence.starterTitle} description={t.licence.starterDescription} icon={PackagePlus}
      size="md" presentation="center" onClose={() => { void dismiss(); }} closeDisabled={busy}>
      <ModalBody>
        <p className="text-ui">{withCore ? t.updateStatus.prepareCoreIntro : t.licence.starterGrants}</p>
        {offer.data.plugins.map(plugin => (
          <section key={plugin.name}>
            <h3 className="flex min-w-0 items-center gap-2 text-card-title"><PluginIcon name={plugin.name} size={24} />{pluginDisplayName(plugin, locale)}</h3>

          </section>
        ))}
      </ModalBody>
      <ModalFooter>
        <Button variant="ghost" disabled={busy} onClick={() => { void dismiss(); }}>{t.licence.starterLater}</Button>
        <Button disabled={busy} onClick={() => { void install(); }}>{busy ? t.licence.starterInstalling : withCore ? t.updateStatus.prepareCore : t.licence.starterInstall}</Button>
      </ModalFooter>
    </Modal>
  );
}
